/**
 * Financial periods, as an Indian audit firm counts them.
 *
 * The year runs 1 April to 31 March, so Q4 is Jan-Mar of the FOLLOWING
 * calendar year. A financial year is identified by the calendar year it
 * starts in, so FY 2026-27 is 2026.
 *
 * Dates are built as strings from integers rather than through `Date`:
 * `toISOString()` is UTC, which shifts a boundary by a day in timezones
 * behind Greenwich.
 */

export type PeriodSpan =
	'FY' | 'H1' | 'H2' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'CUSTOM';

export type Period = {
	/** What the report will call it, e.g. "Q3 FY 2026-27". */
	label: string;
	/** ISO yyyy-mm-dd. */
	start: string;
	end: string;
};

const MONTH = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** The FY that a calendar date falls inside, named by its starting year. */
export function financialYearOf(date: Date = new Date()): number {
	// getMonth() is 0-based, so 3 is April.
	return date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
}

/** 2026 -> "FY 2026-27" */
export function financialYearLabel(fy: number): string {
	return `FY ${fy}-${pad((fy + 1) % 100)}`;
}

/* [start month, start year offset, end month, end year offset]; offset 1 means
   the next calendar year. */
const SPANS: Record<
	Exclude<PeriodSpan, 'CUSTOM'>,
	[number, 0 | 1, number, 0 | 1]
> = {
	FY: [4, 0, 3, 1],
	H1: [4, 0, 9, 0],
	H2: [10, 0, 3, 1],
	Q1: [4, 0, 6, 0],
	Q2: [7, 0, 9, 0],
	Q3: [10, 0, 12, 0],
	Q4: [1, 1, 3, 1],
};

/** Last day of a month. */
const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();

/** The concrete dates and label for one span of one financial year. */
export function periodFor(
	fy: number,
	span: Exclude<PeriodSpan, 'CUSTOM'>,
): Period {
	const [sm, so, em, eo] = SPANS[span];
	const sy = fy + so;
	const ey = fy + eo;
	return {
		label:
			span === 'FY'
				? financialYearLabel(fy)
				: `${span} ${financialYearLabel(fy)}`,
		start: iso(sy, sm, 1),
		end: iso(ey, em, lastDay(ey, em)),
	};
}

/** How the span reads in a dropdown. Months are spelled out because "Q4" alone
 * does not say it means next January. */
export function describeSpan(
	fy: number,
	span: Exclude<PeriodSpan, 'CUSTOM'>,
): string {
	const [sm, so, em, eo] = SPANS[span];
	const sy = fy + so;
	const ey = fy + eo;
	const NAME: Record<Exclude<PeriodSpan, 'CUSTOM'>, string> = {
		FY: 'Full year',
		H1: 'First half',
		H2: 'Second half',
		Q1: 'Quarter 1',
		Q2: 'Quarter 2',
		Q3: 'Quarter 3',
		Q4: 'Quarter 4',
	};
	// Name the year twice only when the period crosses one.
	const range =
		sy === ey
			? `${MONTH[sm - 1]} to ${MONTH[em - 1]} ${sy}`
			: `${MONTH[sm - 1]} ${sy} to ${MONTH[em - 1]} ${ey}`;
	return `${NAME[span]} — ${range}`;
}

export const SPAN_ORDER: Array<Exclude<PeriodSpan, 'CUSTOM'>> = [
	'FY',
	'H1',
	'H2',
	'Q1',
	'Q2',
	'Q3',
	'Q4',
];

/** Options for the period dropdown, for a given financial year. */
export function spanOptions(
	fy: number,
): Array<{ value: string; label: string; group?: string }> {
	return [
		{ value: 'FY', label: describeSpan(fy, 'FY'), group: 'Whole year' },
		{ value: 'H1', label: describeSpan(fy, 'H1'), group: 'Half years' },
		{ value: 'H2', label: describeSpan(fy, 'H2'), group: 'Half years' },
		{ value: 'Q1', label: describeSpan(fy, 'Q1'), group: 'Quarters' },
		{ value: 'Q2', label: describeSpan(fy, 'Q2'), group: 'Quarters' },
		{ value: 'Q3', label: describeSpan(fy, 'Q3'), group: 'Quarters' },
		{ value: 'Q4', label: describeSpan(fy, 'Q4'), group: 'Quarters' },
		{ value: 'CUSTOM', label: 'Custom dates', group: 'Something else' },
	];
}

/** Financial years to offer. Audits usually cover a period that has ended, so
 * the list leans backwards; one year ahead covers an engagement set up early. */
export function financialYearOptions(
	current: number = financialYearOf(),
	back = 5,
	forward = 1,
): Array<{ value: string; label: string }> {
	const out: Array<{ value: string; label: string }> = [];
	for (let fy = current + forward; fy >= current - back; fy--) {
		out.push({ value: String(fy), label: financialYearLabel(fy) });
	}
	return out;
}

/** Work back from a pair of dates to the preset that produced them, so a form
 * opened on existing values does not default to "Custom". */
export function detectPeriod(
	start: string,
	end: string,
	current: number = financialYearOf(),
): { fy: number; span: PeriodSpan } {
	// Wide window either side of today, so an old engagement is still recognised.
	for (let fy = current - 12; fy <= current + 3; fy++) {
		for (const span of SPAN_ORDER) {
			const p = periodFor(fy, span);
			if (p.start === start && p.end === end) return { fy, span };
		}
	}
	return { fy: current, span: 'CUSTOM' };
}
